import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Box, Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Modal from 'modules/core/components/Modal'
import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import Text from 'modules/core/components/TextField'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import FileUploader from 'modules/finance/components/FileUploader'
import { useUpdateWorkArea } from 'modules/locations/hooks/useUpdateWorkArea'
import { useWorkArea } from 'modules/locations/hooks/useWorkArea'
import WorkAreaForm from 'modules/locations/lib/forms/WorkAreaForm'
import { SOURCE } from 'modules/shift_management/hooks/reducers/schedule'
import { useCreateWorkAreaMap } from 'modules/space_control/hooks/useCreateWorkAreaMap'
import { useDeleteWorkAreaMap } from 'modules/space_control/hooks/useDeleteWorkAreaMap'
import { useUpdateWorkAreaMap } from 'modules/space_control/hooks/useUpdateWorkAreaMap'
import { useWorkAreaMaps } from 'modules/space_control/hooks/useWorkAreaMaps'

interface Props {
  onClose: () => void
  workAreaId: string
  locationId: string
}

const EditWorkArea = ({ locationId, workAreaId, onClose }: Props) => {
  const { workArea } = useWorkArea({ id: workAreaId })

  const { workAreaMapsById, loading: _loadingWorkAreaMap } = useWorkAreaMaps({
    workAreaIds: [workAreaId],
  })

  const workAreaMap = workAreaMapsById[workAreaId]
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const source = queryParams.get('source')
  const comesFromShiftManagement = source === SOURCE
  const viewType = queryParams.get('viewType')

  const [selectedFile, setSelectedFile] = useState<File | undefined>(undefined)
  const [existingFile, setExistingFile] = useState<File | undefined>(undefined)
  const [mapDeleted, setMapDeleted] = useState(false)

  const workAreaForm = useLazyRef(
    () =>
      new WorkAreaForm({
        name: workArea?.name,
        location_id: locationId,
      })
  ).current

  const isComplete = !!workAreaForm.get('name').value

  const { updateWorkArea } = useUpdateWorkArea()
  const { updateWorkAreaMap } = useUpdateWorkAreaMap()
  const { createWorkAreaMap } = useCreateWorkAreaMap()
  const { deleteWorkAreaMap } = useDeleteWorkAreaMap()

  const onSubmit = async (closePortal: ClosePortalFn) => {
    const result = await updateWorkArea({
      id: workAreaId,
      name: workAreaForm.get('name').value,
    })

    const handledResult = handleResult(result, (errors) => {
      Object.entries(errors).forEach(([field, message]) => {
        workAreaForm.setErrors({ [field]: message })
      })
    })

    if (!handledResult.success) return

    if (selectedFile) {
      let handledMapResult: { success: boolean } | undefined
      if (workAreaMap?.id) {
        const updateResult = await updateWorkAreaMap({
          id: workAreaMap.id,
          image: selectedFile,
        })
        handledMapResult = handleResult(updateResult, () => {})
      } else {
        const createResult = await createWorkAreaMap({
          workAreaId: workAreaId,
          image: selectedFile,
        })
        handledMapResult = handleResult(createResult, () => {})
      }
      if (!handledMapResult?.success) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('settings.locations.location.work_areas.map.save_map_error_message'),
        })
        return
      }
    }

    track(trackingEvents.workAreas.editWorkAreaSubmit, {
      event_category: 'Workplaces',
      workarea_id: workAreaId,
      location_id: locationId,
      workarea_name: workAreaForm.get('name').value,
      has_map: Boolean(selectedFile),
      from: comesFromShiftManagement ? 'shift_management' : 'locations_settings',
    })

    closePortal()
  }

  const handleDeleteMap = async () => {
    if (workAreaMap?.id) {
      await deleteWorkAreaMap({ id: workAreaMap.id })
      setSelectedFile(undefined)
      setExistingFile(undefined)
      setMapDeleted(true)
    }
  }

  const modalTitle = i18n.t('settings.locations.location.work_areas.edit.title')
  const modalDescription = i18n.t('settings.locations.location.work_areas.edit.description')
  const modalButton = i18n.t('settings.locations.location.work_areas.edit.button')

  const existingImage = workAreaMap?.image?.url

  useEffect(() => {
    if (existingImage && !mapDeleted) {
      fetch(existingImage)
        .then((res) => res.blob())
        .then((blob) => {
          const file = new File([blob], 'existing-map.jpg', { type: blob.type })
          setExistingFile(file)
          setSelectedFile(file)
        })
    }
  }, [existingImage, mapDeleted])

  useEffect(() => {
    setMapDeleted(false)
    setExistingFile(undefined)
    setSelectedFile(undefined)
  }, [workAreaId])

  return (
    <Modal
      illustration="calendar"
      title={modalTitle}
      description={modalDescription}
      onSubmit={onSubmit}
      onClose={onClose}
      footer={
        <Button disabled={!isComplete} submit>
          {modalButton}
        </Button>
      }
    >
      <Box gap="s16">
        <Text
          label={i18n.t('settings.locations.location.work_areas.field_name')}
          value={workAreaForm.get('name').value}
          onChange={(value: string) => workAreaForm.setValues({ name: value })}
        />
        <Box gap="s8">
          <FileUploader
            placeholder={i18n.t('settings.locations.location.work_areas.map.image_placeholder')}
            description=""
            selectedFile={selectedFile || existingFile}
            onDrop={(files: File[]) => setSelectedFile(files[0])}
            acceptedFormats={{ 'image/*': [] }}
          />
          {workAreaMap?.id && (
            <Button type="secondary" onClick={handleDeleteMap}>
              {i18n.t('settings.locations.location.work_areas.map.delete_map')}
            </Button>
          )}
        </Box>
      </Box>
    </Modal>
  )
}

export default observer(EditWorkArea)
